import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createJob, updateJob, JOB_STATUS } from '../services/jobService';
import { getCompanyProfileByUserId } from '../services/companyService';
import { checkPackageLimit, updateUsageStats, getUserActiveSubscription } from '../services/subscriptionService';
import { generateTopicsFromJob, sendTopicNotification } from '../services/fcmService';
import { initiateRazorpayPayment, calculateGST, formatAmount } from '../services/razorpayService';
import { sendJobPostingNotification } from '../services/whatsappService';
import ChipInput from '../components/ChipInput';
import SmartDropdown from '../components/SmartDropdown';
import EnhancedChipInput from '../components/EnhancedChipInput';
import SimpleDropdown from '../components/SimpleDropdown';
import Footer from '../components/Footer';
import {
  getEducationSuggestions,
  getBenefitsSuggestions,
  getDepartmentSuggestions,
  getSkillsSuggestions,
  getPreferredSkillsSuggestions,
  getRolesSuggestions,
  getResponsibilitiesSuggestions,
  generateJobDescription,
  saveCustomSuggestion,
  saveMultipleSuggestions
} from '../services/aiSuggestionsService';

// Local storage key for draft data
const getDraftStorageKey = (userId) => `postJobDraft_${userId}`;

const PostJob = ({ user, userData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  const [fetchingCompany, setFetchingCompany] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editJobId, setEditJobId] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState({
    education: [],
    benefits: [],
    departments: [],
    skills: [],
    preferredSkills: [],
    roles: [],
    responsibilities: []
  });
  const [isLoadingSkills, setIsLoadingSkills] = useState(false);
  const [isLoadingPreferredSkills, setIsLoadingPreferredSkills] = useState(false);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [isLoadingResponsibilities, setIsLoadingResponsibilities] = useState(false);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [allowIndustryEdit, setAllowIndustryEdit] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [payNowDialog, setPayNowDialog] = useState({ visible: false, message: '' });
  const payNowResolverRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    company: '',
    companyLogo: '',
    companyDetails: null,
    location: {
      city: '',
      state: '',
      country: 'India',
      remote: false,
      hybrid: false
    },
    jobType: 'full-time',
    workMode: 'Work From Office',
    experienceLevel: 'mid',
    department: '',
    industry: '',
    salary: {
      min: '',
      max: '',
      currency: 'INR',
      period: 'monthly',
      negotiable: false
    },
    description: '',
    requirements: [],
    responsibilities: [],
    benefits: [],
    requiredSkills: [],
    preferredSkills: [],
    education: '',
    certifications: [],
    applicationDeadline: '',
    applicationInstructions: '',
    gender: 'both',
    experienceRequired: '',
    status: JOB_STATUS.ACTIVE,
    featured: false,
    urgent: false
  });

  const showPayNowDialog = (message) => {
    return new Promise((resolve) => {
      payNowResolverRef.current = resolve;
      setPayNowDialog({ visible: true, message });
    });
  };

  const closePayNowDialog = (accepted, navigateToPackages = false) => {
    setPayNowDialog(prev => ({ ...prev, visible: false }));
    if (payNowResolverRef.current) {
      payNowResolverRef.current(accepted);
      payNowResolverRef.current = null;
    }

    if (accepted && navigateToPackages) {
      navigate('/packages');
    }
  };

  // Fetch company details and AI suggestions on component mount
  useEffect(() => {
    const fetchData = async () => {
      if (user?.uid) {
        try {
          // Fetch company data
          const result = await getCompanyProfileByUserId(user.uid);
          if (result.success) {
            setCompanyData(result.data);
            // Pre-fill form with company data
            setFormData(prev => ({
              ...prev,
              company: prev.company || result.data.companyName || '',
              companyLogo: prev.companyLogo || result.data.logoUrl || '',
              companyDetails: prev.companyDetails || result.data,
              location: {
                ...prev.location,
                city: prev.location?.city || result.data.city || '',
                state: prev.location?.state || result.data.state || '',
                country: prev.location?.country || result.data.country || 'India'
              },
              industry: prev.industry || result.data.industry || ''
            }));
          }

          // Fetch current subscription (only for new posts, not edits)
          if (!location.state?.editMode) {
            const subscriptionResult = await getUserActiveSubscription(user.uid);
            if (subscriptionResult.success && subscriptionResult.subscription) {
              setCurrentSubscription(subscriptionResult.subscription);
            }
          }

          // Load AI suggestions

          await loadAISuggestions();
        } catch (error) {
          console.error('Error fetching data:', error);
        } finally {
          setFetchingCompany(false);
          setCheckingSubscription(false);
        }
      } else {
        setFetchingCompany(false);
        setCheckingSubscription(false);
      }
    };

    fetchData();
  }, [user]);

  // Load draft from localStorage when available (for new posts only)
  useEffect(() => {
    if (isEditMode || !user?.uid || draftLoaded) {
      return;
    }

    try {
      const savedDraft = localStorage.getItem(getDraftStorageKey(user.uid));
      if (savedDraft) {
        const parsedDraft = JSON.parse(savedDraft);
        console.log('📋 [PostJob] Loading saved draft from localStorage');

        // Merge saved draft with current form data, preserving company data
        setFormData(prev => ({
          ...prev,
          ...parsedDraft,
          company: parsedDraft.company || prev.company,
          companyLogo: parsedDraft.companyLogo || prev.companyLogo,
          companyDetails: parsedDraft.companyDetails || prev.companyDetails,
          location: {
            ...prev.location,
            ...parsedDraft.location,
            city: parsedDraft.location?.city || prev.location?.city || '',
            state: parsedDraft.location?.state || prev.location?.state || '',
            country: parsedDraft.location?.country || prev.location?.country || 'India'
          },
          industry: parsedDraft.industry || prev.industry
        }));
      }
    } catch (draftError) {
      console.warn('⚠️ [PostJob] Failed to load saved job draft:', draftError);
    } finally {
      setDraftLoaded(true);
    }
  }, [user?.uid, isEditMode, draftLoaded]);

  // Persist draft to localStorage whenever form data changes (new posts only)
  useEffect(() => {
    if (isEditMode || !user?.uid || !draftLoaded) {
      return;
    }

    // Debounce the save operation to avoid excessive localStorage writes
    const timeoutId = setTimeout(() => {
      try {
        console.log('💾 [PostJob] Saving draft to localStorage');
        localStorage.setItem(getDraftStorageKey(user.uid), JSON.stringify(formData));
      } catch (draftError) {
        console.warn('⚠️ [PostJob] Failed to save job draft:', draftError);
      }
    }, 1000); // Save after 1 second of inactivity

    return () => clearTimeout(timeoutId);
  }, [formData, user?.uid, isEditMode, draftLoaded]);

  // Load AI suggestions
  const loadAISuggestions = async () => {
    try {
      const [education, benefits, departments] = await Promise.all([
        getEducationSuggestions(user?.uid),
        getBenefitsSuggestions(user?.uid),
        getDepartmentSuggestions(user?.uid)
      ]);

      setAiSuggestions(prev => ({
        ...prev,
        education,
        benefits,
        departments,
        skills: getSkillsSuggestions(formData.education || '')
      }));
    } catch (error) {
      console.error('Error loading AI suggestions:', error);
    }
  };

  // Update suggestions when form data changes
  useEffect(() => {
    if (formData.education) {
      setIsLoadingSkills(true);
      // Simulate AI processing time
      setTimeout(() => {
        const skills = getSkillsSuggestions(formData.education, user?.uid);
        setAiSuggestions(prev => ({ ...prev, skills }));
        setIsLoadingSkills(false);
      }, 1500);
    }
  }, [formData.education, user?.uid]);

  useEffect(() => {
    if (formData.requiredSkills.length > 0 && formData.education) {
      setIsLoadingPreferredSkills(true);
      // Simulate AI processing time
      setTimeout(() => {
        const preferredSkills = getPreferredSkillsSuggestions(formData.requiredSkills, formData.education);
        setAiSuggestions(prev => ({ ...prev, preferredSkills }));
        setIsLoadingPreferredSkills(false);
      }, 1200);
    }
  }, [formData.requiredSkills, formData.education]);

  useEffect(() => {
    if (formData.industry && formData.experienceLevel) {
      setIsLoadingRoles(true);
      // Simulate AI processing time
      setTimeout(() => {
        const roles = getRolesSuggestions(formData.industry, formData.experienceLevel, user?.uid);
        setAiSuggestions(prev => ({ ...prev, roles }));
        setIsLoadingRoles(false);
      }, 1800);
    }
  }, [formData.industry, formData.experienceLevel, user?.uid]);

  useEffect(() => {
    if (formData.requirements.length > 0 && formData.experienceLevel) {
      setIsLoadingResponsibilities(true);
      // Simulate AI processing time
      setTimeout(() => {
        const responsibilities = getResponsibilitiesSuggestions(formData.requirements, formData.experienceLevel);
        setAiSuggestions(prev => ({ ...prev, responsibilities }));
        setIsLoadingResponsibilities(false);
      }, 1600);
    }
  }, [formData.requirements, formData.experienceLevel]);

  // Handle edit mode
  useEffect(() => {
    if (location.state?.editMode && location.state?.jobData) {
      console.log('🔧 [PostJob] Edit mode detected, loading job data:', location.state.jobData);
      setIsEditMode(true);
      setEditJobId(location.state.jobId);

      const jobData = location.state.jobData;
      setFormData({
        title: jobData.title || '',
        company: jobData.company || '',
        companyLogo: jobData.companyLogo || '',
        companyDetails: jobData.companyDetails || null,
        location: {
          city: jobData.location?.city || '',
          state: jobData.location?.state || '',
          country: jobData.location?.country || 'India',
          remote: jobData.location?.remote || false,
          hybrid: jobData.location?.hybrid || false
        },
        jobType: jobData.jobType || 'full-time',
        workMode: jobData.workMode === 'onsite' ? 'In Field' :
          jobData.workMode === 'remote' ? 'Work From Home' :
            (jobData.workMode || 'Work From Office'),
        experienceLevel: jobData.experienceLevel || 'mid',
        department: jobData.department || '',
        industry: jobData.industry || '',
        salary: {
          min: jobData.salary?.min || '',
          max: jobData.salary?.max || '',
          currency: jobData.salary?.currency || 'INR',
          period: jobData.salary?.period || 'monthly',
          negotiable: jobData.salary?.negotiable || false
        },
        description: jobData.description || '',
        requirements: jobData.requirements || [],
        responsibilities: jobData.responsibilities || [],
        benefits: jobData.benefits || [],
        requiredSkills: jobData.requiredSkills || [],
        preferredSkills: jobData.preferredSkills || [],
        education: jobData.education || '',
        certifications: jobData.certifications || [],
        applicationDeadline: jobData.applicationDeadline ?
          new Date(jobData.applicationDeadline).toISOString().split('T')[0] : '',
        applicationInstructions: jobData.applicationInstructions || '',
        gender: jobData.gender || 'both',
        experienceRequired: jobData.experienceRequired || '',
        status: jobData.status || JOB_STATUS.ACTIVE,
        featured: jobData.featured || false,
        urgent: jobData.urgent || false
      });
    }
  }, [location.state]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  // Handle custom suggestions
  const handleCustomSuggestion = async (type, value) => {
    if (user?.uid && value.trim()) {
      await saveCustomSuggestion(user.uid, type, value.trim());
    }
  };

  // Auto-generate job description
  const handleGenerateDescription = async () => {
    setIsGeneratingDescription(true);
    try {
      // Simulate AI processing delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      const generatedDescription = generateJobDescription(formData);
      setFormData(prev => ({ ...prev, description: generatedDescription }));
    } catch (error) {
      console.error('Error generating description:', error);
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  // Enhanced regenerate functionality with multiple formats
  const handleRegenerateDescription = async () => {
    setIsGeneratingDescription(true);
    try {
      // Enhanced AI processing with more sophisticated generation
      await new Promise(resolve => setTimeout(resolve, 2500));

      // Generate enhanced description with multiple formats and sections
      const enhancedDescription = generateEnhancedJobDescription({
        ...formData,
        companySize: companyData?.size || 'Growing',
        companyStage: companyData?.stage || 'Established',
        companyName: companyData?.name || formData.company
      });
      setFormData(prev => ({ ...prev, description: enhancedDescription }));
    } catch (error) {
      console.error('Error regenerating description:', error);
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  // Enhanced job description generator
  const generateEnhancedJobDescription = (jobData) => {
    const {
      title,
      company,
      companyName,
      industry,
      experienceLevel,
      requiredSkills = [],
      preferredSkills = [],
      requirements = [],
      responsibilities = [],
      benefits = [],
      workMode,
      jobType,
      companySize,
      companyStage
    } = jobData;

    const experienceLevels = {
      'entry': 'Entry-Level',
      'mid': 'Mid-Level',
      'senior': 'Senior-Level',
      'executive': 'Executive-Level'
    };

    const workModes = {
      'onsite': 'On-site',
      'remote': 'Remote',
      'hybrid': 'Hybrid'
    };

    return `🚀 **${title} - ${experienceLevels[experienceLevel] || 'Professional'} Position**

**About ${companyName || company}:**
We are a ${companyStage} ${companySize} company in the ${industry} industry, committed to innovation and excellence. Join our dynamic team and be part of our exciting journey towards transforming the industry landscape.

---

## 🎯 **Position Overview**
We are seeking a highly motivated and skilled ${title} to join our growing team. This ${jobType} ${workModes[workMode] || workMode} position offers an excellent opportunity to work with cutting-edge technologies and contribute to meaningful projects that make a real impact.

---

## 💼 **Key Responsibilities**
${responsibilities.length > 0 ? responsibilities.map((resp, index) => `• **${resp}** - Drive excellence in execution and delivery`).join('\n') : `• Lead and execute ${title.toLowerCase()} initiatives with precision and innovation
• Collaborate with cross-functional teams to deliver exceptional results
• Contribute to strategic planning and implementation of key projects
• Maintain high standards of quality and performance in all deliverables`}

---

## 🎓 **Required Qualifications**
${requirements.length > 0 ? requirements.map((req, index) => `• **${req}** - Essential for success in this role`).join('\n') : `• Strong foundation in ${industry.toLowerCase()} principles and practices
• Proven track record of delivering results in ${experienceLevel} positions
• Excellent problem-solving and analytical thinking capabilities`}

---

## 🛠️ **Technical Skills Required**
${requiredSkills.length > 0 ? requiredSkills.map((skill, index) => `• **${skill}** - Core competency required`).join('\n') : `• Proficiency in industry-standard tools and technologies
• Strong technical foundation relevant to ${title.toLowerCase()} role
• Ability to learn and adapt to new technologies quickly`}

---

## ⭐ **Preferred Skills & Experience**
${preferredSkills.length > 0 ? preferredSkills.map((skill, index) => `• **${skill}** - Added advantage for candidates`).join('\n') : `• Additional certifications or specialized training
• Experience with advanced tools and methodologies
• Leadership and mentoring capabilities`}

---

## 🎁 **What We Offer**
${benefits.length > 0 ? benefits.map((benefit, index) => `• **${benefit}** - Comprehensive package for your growth`).join('\n') : `• **Competitive Compensation** - Market-leading salary and benefits
• **Professional Growth** - Continuous learning and development opportunities
• **Work-Life Balance** - Flexible working arrangements and wellness programs
• **Innovation Culture** - Work with latest technologies in a collaborative environment`}

---

## 🌟 **Why Choose Us?**
• **Impact**: Work on projects that matter and make a difference
• **Growth**: Accelerate your career with mentorship and learning opportunities
• **Culture**: Join a diverse, inclusive, and supportive work environment
• **Innovation**: Be at the forefront of ${industry.toLowerCase()} technology and trends

---

**Ready to take your career to the next level?** 

We're looking for passionate individuals who share our vision and values. If you're excited about this opportunity and meet our requirements, we'd love to hear from you!

*${companyName || company} is an equal opportunity employer committed to diversity and inclusion.*`;
  };

  // Check if description can be auto-generated
  const canGenerateDescription = () => {
    return formData.title && formData.company && formData.industry &&
      (formData.requirements.length > 0 || formData.responsibilities.length > 0 || formData.requiredSkills.length > 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // For new job posts (not edits), check package limits
      if (!isEditMode) {
        console.log('🔍 [PostJob] Checking package limits...');
        const limitCheck = await checkPackageLimit(user.uid, 'jobPosting');

        if (!limitCheck.success) {
          setLoading(false);

          if (limitCheck.needsSubscription) {
            await showPayNowDialog(
              '📦 You need an active package to post jobs.\n\n' +
              'Would you like to purchase a package now?'
            );
            return;
          }

          if (limitCheck.limitReached) {
            const shouldUpgrade = window.confirm(
              '⚠️ You have reached your job posting limit!\n\n' +
              `Current usage: ${currentSubscription?.usageStats?.jobPostingsUsed || 0}/${currentSubscription?.maxJobPostings || 0}\n\n` +
              'Would you like to upgrade your package?'
            );
            if (shouldUpgrade) {
              navigate('/packages');
            }
            return;
          }

          alert('❌ ' + limitCheck.error);
          return;
        }

        console.log('✅ [PostJob] Package limit check passed');

        // If posting as featured, check featured jobs limit
        if (formData.featured) {
          console.log('🔍 [PostJob] Checking featured jobs limit...');
          const featuredLimitCheck = await checkPackageLimit(user.uid, 'featuredJob');

          if (!featuredLimitCheck.success) {
            setLoading(false);

            if (featuredLimitCheck.limitReached) {
              const shouldUpgrade = window.confirm(
                '⭐ Featured Jobs Limit Reached!\n\n' +
                `You have used all ${currentSubscription?.featuredJobs || 0} featured job slots.\n\n` +
                'Featured jobs are a subset of your total job postings.\n' +
                'Would you like to upgrade your package for more featured slots?'
              );
              if (shouldUpgrade) {
                navigate('/packages');
              }
              return;
            }

            alert('❌ ' + featuredLimitCheck.error);
            return;
          }

          console.log('✅ [PostJob] Featured jobs limit check passed');
        }
      }

      // Save custom suggestions to Firebase
      const customData = {
        requirements: formData.requirements,
        responsibilities: formData.responsibilities,
        benefits: formData.benefits,
        requiredSkills: formData.requiredSkills,
        preferredSkills: formData.preferredSkills
      };

      if (user?.uid) {
        await saveMultipleSuggestions(user.uid, customData);
      }

      // Clean up data for job creation/update
      const cleanedData = {
        ...formData,
        // Include company details
        companyLogo: formData.companyLogo,
        companyDetails: formData.companyDetails
      };

      let result;
      if (isEditMode && editJobId) {
        console.log('🔧 [PostJob] Updating job:', editJobId);
        result = await updateJob(editJobId, cleanedData, user.uid);
      } else {
        console.log('➕ [PostJob] Creating new job');
        result = await createJob(cleanedData, user.uid);
      }

      if (result.success) {
        // For new job posts, update usage stats and clear draft
        if (!isEditMode) {
          if (currentSubscription) {
            console.log('📊 [PostJob] Updating usage stats...');
            await updateUsageStats(currentSubscription.id, 'jobPostingsUsed', 1);

            // If posted as featured, also increment featured jobs counter
            if (formData.featured) {
              console.log('⭐ [PostJob] Updating featured jobs usage stats...');
              await updateUsageStats(currentSubscription.id, 'featuredJobsUsed', 1);
            }
          }

          // Clear the saved draft from localStorage on successful post
          if (user?.uid) {
            try {
              localStorage.removeItem(getDraftStorageKey(user.uid));
              console.log('🗑️ [PostJob] Cleared saved draft from localStorage');
            } catch (draftError) {
              console.warn('⚠️ [PostJob] Failed to clear saved job draft:', draftError);
            }
          }

          // Send WhatsApp notification to employer using new service
          try {
            console.log('📱 [PostJob] Initiating WhatsApp notification...');

            const employerName = userData?.firstName ? `${userData.firstName} ${userData.lastName || ''}`.trim() : user.displayName || 'Employer';
            const employerPhone = userData?.phone || '';
            const remainingJobPostings = (currentSubscription?.maxJobPostings || 0) - (currentSubscription?.usageStats?.jobPostingsUsed || 0);

            // Prepare user info for WhatsApp service
            const userInfo = {
              name: employerName,
              phone: employerPhone
            };

            // Prepare package info for WhatsApp service
            const packageInfo = {
              remainingJobs: remainingJobPostings,
              totalJobs: currentSubscription?.maxJobPostings || 0,
              usedJobs: (currentSubscription?.usageStats?.jobPostingsUsed || 0) + 1
            };

            // Send WhatsApp notification using the new service
            const whatsappResult = await sendJobPostingNotification(cleanedData, userInfo, packageInfo);

            if (whatsappResult.success) {
              console.log('✅ [PostJob] WhatsApp notification sent successfully via webhook');
              console.log('📊 [PostJob] WhatsApp response:', whatsappResult.data);
            } else {
              console.warn('⚠️ [PostJob] Failed to send WhatsApp notification:', whatsappResult.error);
              console.warn('🔍 [PostJob] WhatsApp failure details:', whatsappResult);
            }
          } catch (whatsappError) {
            console.error('❌ [PostJob] Error in WhatsApp notification process:', whatsappError);
            console.error('🔍 [PostJob] WhatsApp error stack:', whatsappError.stack);
            // Don't fail the job posting if WhatsApp fails
          }
        }

        // Send topic-based notifications for new job posts
        if (!isEditMode && cleanedData.status === JOB_STATUS.ACTIVE) {
          try {
            console.log('🔔 [PostJob] Sending topic-based notifications...');
            const topics = generateTopicsFromJob(cleanedData);

            if (topics.length > 0) {
              await sendTopicNotification(topics, {
                title: `New Job: ${cleanedData.title}`,
                body: `${cleanedData.company} is hiring for ${cleanedData.title} position. Apply now!`,
                data: {
                  jobId: result.jobId,
                  jobTitle: cleanedData.title,
                  company: cleanedData.company,
                  type: 'new_job'
                }
              });
              console.log('✅ [PostJob] Notifications sent to topics:', topics);
            }
          } catch (notifError) {
            console.error('❌ [PostJob] Error sending notifications:', notifError);
            // Don't fail the job posting if notifications fail
          }
        }

        const message = isEditMode ? 'Job updated successfully!' : 'Job posted successfully!';
        navigate('/manage-jobs', {
          state: { message }
        });
      } else {
        const action = isEditMode ? 'updating' : 'posting';
        alert(`Error ${action} job: ` + result.error);
      }
    } catch (error) {
      const action = isEditMode ? 'updating' : 'posting';
      alert(`Error ${action} job: ` + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (fetchingCompany) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading company details...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 pb-24 lg:pb-0">
        {/* Enhanced Header Section */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2h8zM8 14v.01M12 14v.01M16 14v.01" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                    {isEditMode ? 'Edit Job Posting' : 'Create Job Posting'}
                  </h1>
                  <p className="text-gray-600 mt-1 flex items-center gap-2">
                    <span className="w-2 h-2 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full animate-pulse"></span>
                    {isEditMode
                      ? 'Update your job posting with AI-powered enhancements'
                      : 'Craft the perfect job posting with AI assistance'
                    }
                  </p>
                </div>
              </div>

              {/* AI Status Indicator */}
              <div className="hidden md:flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-purple-50 to-pink-50 rounded-full border border-purple-200">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full animate-ping"></div>
                  <span className="text-sm font-semibold text-purple-700">AI-Enhanced</span>
                </div>
                <div className="w-px h-4 bg-purple-300"></div>
                <span className="text-xs text-purple-600">Smart suggestions enabled</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Package Status Banner */}
          {!isEditMode && currentSubscription && (
            <div className="mb-6 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border-2 border-green-300 shadow-lg">
              <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-4">
                <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-green-900 mb-2">
                      Active Package: {currentSubscription.packageName}
                    </h3>
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-green-700">Job Postings:</span>
                        <span className="text-lg font-bold text-green-900">
                          {currentSubscription.usageStats?.jobPostingsUsed || 0} / {currentSubscription.maxJobPostings || 0}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border-2 border-green-400">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                        <span className="text-sm font-semibold text-green-800">
                          {(currentSubscription.maxJobPostings || 0) - (currentSubscription.usageStats?.jobPostingsUsed || 0)} Remaining
                        </span>
                      </div>
                    </div>
                    {/* Progress Bar */}
                    <div className="mt-3 w-full bg-green-200 rounded-full h-2.5">
                      <div
                        className="bg-green-600 h-2.5 rounded-full transition-all duration-300"
                        style={{
                          width: `${((currentSubscription.usageStats?.jobPostingsUsed || 0) / (currentSubscription.maxJobPostings || 1)) * 100}%`
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/packages')}
                  className="px-6 py-3 bg-white text-green-700 font-bold rounded-lg hover:bg-green-100 transition-colors border-2 border-green-400 shadow-md hover:shadow-lg"
                >
                  Upgrade Package
                </button>
              </div>
            </div>
          )}

          {/* No Package Warning */}
          {!isEditMode && !currentSubscription && !checkingSubscription && (
            <div className="mb-6 p-6 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl border-2 border-yellow-300 shadow-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-yellow-900">
                      ⚠️ No Active Package
                    </h3>
                    <p className="text-sm text-yellow-700">
                      You need to purchase a package to post jobs. Choose from our flexible plans!
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/packages')}
                  className="px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold rounded-lg hover:from-yellow-600 hover:to-orange-600 transition-all shadow-lg"
                >
                  Purchase Package
                </button>
              </div>
            </div>
          )}

          <div className="mb-6">

            {/* Company Info Display */}
            {companyData && (
              <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    {/* Company Logo with Fallback */}
                    <div className="w-16 h-16 rounded-xl shadow-md overflow-hidden bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                      {companyData.logoUrl || companyData.logo ? (
                        <img
                          src={companyData.logoUrl || companyData.logo}
                          alt={companyData.companyName || companyData.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      {/* Fallback Icon */}
                      <div className="w-full h-full flex items-center justify-center text-blue-600" style={{ display: companyData.logoUrl || companyData.logo ? 'none' : 'flex' }}>
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                    </div>
                    {/* Status Indicator */}
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900">{companyData.companyName || companyData.name || 'Company Name'}</h3>
                    <p className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                      <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></span>
                      <span>{companyData.industry || 'Industry'}</span>
                      {companyData.location && (
                        <>
                          <span>•</span>
                          <span>{companyData.location}</span>
                        </>
                      )}
                    </p>
                    {/* Additional Company Info */}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        </svg>
                        Verified Company
                      </span>
                      <span className="flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Active Profile
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Information */}
            <div className="bg-white shadow-xl rounded-2xl p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900">Basic Information</h2>
                <div className="flex-1 h-px bg-gradient-to-r from-blue-200 to-transparent"></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Title / नोकरीचे शीर्षक *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. Senior Software Engineer"
                  />
                </div>

                <div>
                  <SimpleDropdown
                    label="Department / विभाग"
                    value={formData.department}
                    onChange={(value) => setFormData(prev => ({ ...prev, department: value }))}
                    options={aiSuggestions.departments}
                    placeholder="Select department..."
                    required={false}
                    allowCustom={true}
                    onAddCustom={(value) => handleCustomSuggestion('departments', value)}
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Industry / उद्योग *
                      {companyData && !allowIndustryEdit && <span className="text-sm text-gray-500">(from company profile)</span>}
                    </label>
                    {companyData && (
                      <button
                        type="button"
                        onClick={() => setAllowIndustryEdit(!allowIndustryEdit)}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        {allowIndustryEdit ? 'Use Company Industry' : 'Change Industry'}
                      </button>
                    )}
                  </div>
                  {companyData?.industry && !allowIndustryEdit ? (
                    <input
                      type="text"
                      value={formData.industry}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
                    />
                  ) : (
                    <SimpleDropdown
                      label=""
                      value={formData.industry}
                      onChange={(value) => setFormData(prev => ({ ...prev, industry: value }))}
                      options={[
                        'Automobile / Auto Ancillary',
                        'Engineering / Fabrication',
                        'Foundry / Casting',
                        'Plastic / Injection Moulding',
                        'Textile / Garments',
                        'Food Processing',
                        'Packaging Industry',
                        'Chemical Industry',
                        'Pharma Manufacturing',
                        'Electrical / Electronics Manufacturing',
                        'Paper / Printing',
                        'Steel / Metal Industry',
                        'Security Services',
                        'Housekeeping Services',
                        'Facility Management',
                        'Construction / Civil',
                        'Maintenance & Repair',
                        'Warehouse / Logistics',
                        'Courier & Delivery',
                        'Driver / Transport Services',
                        'Manpower / Staffing Agency',
                        'Pest Control Services',
                        'Hotel / Hospitality',
                        'Restaurant / Cafe / Food Service',
                        'Travel & Tourism',
                        'Event Management',
                        'Salon / Spa / Beauty',
                        'Fitness / Gym',
                        'Laundry Services',
                        'FMCG',
                        'Supermarket / Hypermarket',
                        'Showroom / Electronics Retail',
                        'Jewellery Retail',
                        'Mobile Shop / Electronics Shop',
                        'Automobile Showroom',
                        'BPO / Call Center',
                        'KPO / Back Office',
                        'E-commerce',
                        'IT Services / Software',
                        'Digital Marketing Agency',
                        'Advertising / Marketing Agency',
                        'HR / Recruitment Agency',
                        'Hospital / Clinic',
                        'Diagnostic Lab',
                        'Medical Store / Pharmacy',
                        'Medical Equipment',
                        'Nursing / Care Services',
                        'School',
                        'College',
                        'Coaching Classes',
                        'EdTech / Online Education',
                        'Agriculture / Agri Services',
                        'Dairy / Poultry',
                        'Cold Storage',
                        'Seeds / Fertilizer',
                        'Agro Processing',
                        'Other'
                      ]}
                      placeholder="Select industry..."
                      required
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Type / नोकरीचा प्रकार *
                  </label>
                  <select
                    name="jobType"
                    value={formData.jobType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="full-time">Full-time</option>
                    <option value="part-time">Part-time</option>
                    <option value="contract">Contract</option>
                    <option value="internship">Internship</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Work Mode / कार्य पद्धत *
                  </label>
                  <select
                    name="workMode"
                    value={formData.workMode}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Work From Home">Work From Home</option>
                    <option value="In Field">In Field</option>
                    <option value="Work From Office">Work From Office</option>
                    <option value="Hybrid">Hybrid</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Experience Level / अनुभव स्तर *
                  </label>
                  <select
                    name="experienceLevel"
                    value={formData.experienceLevel}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="entry">Entry Level</option>
                    <option value="mid">Mid Level</option>
                    <option value="senior">Senior Level</option>
                    <option value="executive">Executive</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gender Preference / लिंग प्राधान्य *
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="both">Both (Male & Female) / दोन्ही</option>
                    <option value="male">Male / पुरुष</option>
                    <option value="female">Female / स्त्री</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Experience Required (Years) / आवश्यक अनुभव (वर्षे) *
                  </label>
                  <input
                    type="text"
                    name="experienceRequired"
                    value={formData.experienceRequired}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. 0-1, 1-3, 3-5, 5+ / उदा. ०-१, १-३"
                  />
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="bg-white shadow-xl rounded-2xl p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-pink-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900">Location Details</h2>
                <div className="flex-1 h-px bg-gradient-to-r from-red-200 to-transparent"></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City / शहर * {companyData && <span className="text-sm text-gray-500">(from company profile)</span>}
                  </label>
                  <input
                    type="text"
                    name="location.city"
                    value={formData.location.city}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    readOnly={!!companyData?.city}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State / राज्य * {companyData && <span className="text-sm text-gray-500">(from company profile)</span>}
                  </label>
                  <input
                    type="text"
                    name="location.state"
                    value={formData.location.state}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    readOnly={!!companyData?.state}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country / देश * {companyData && <span className="text-sm text-gray-500">(from company profile)</span>}
                  </label>
                  <input
                    type="text"
                    name="location.country"
                    value={formData.location.country}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    readOnly={!!companyData?.country}
                  />
                </div>
              </div>

              <div className="mt-4 flex space-x-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="location.remote"
                    checked={formData.location.remote}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Remote work available / रिमोट काम उपलब्ध</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="location.hybrid"
                    checked={formData.location.hybrid}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Hybrid work available / हायब्रिड काम उपलब्ध</span>
                </label>
              </div>
            </div>

            {/* Compensation */}
            <div className="bg-white shadow-xl rounded-2xl p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900">Salary Information</h2>
                <div className="flex-1 h-px bg-gradient-to-r from-yellow-200 to-transparent"></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Min Salary / किमान पगार
                  </label>
                  <input
                    type="number"
                    name="salary.min"
                    value={formData.salary.min}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Salary / कमाल पगार
                  </label>
                  <input
                    type="number"
                    name="salary.max"
                    value={formData.salary.max}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Currency / चलन
                  </label>
                  <select
                    name="salary.currency"
                    value={formData.salary.currency}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="INR">INR</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Period / कालावधी
                  </label>
                  <select
                    name="salary.period"
                    value={formData.salary.period}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="yearly">Yearly</option>
                    <option value="monthly">Monthly</option>
                    <option value="hourly">Hourly</option>
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="salary.negotiable"
                    checked={formData.salary.negotiable}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Salary is negotiable / पगार वाटाघाटीयोग्य आहे</span>
                </label>
              </div>
            </div>


            {/* Skills & Qualifications */}
            <div className="bg-white shadow-xl rounded-2xl p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900">Skills & Qualifications</h2>
                <div className="flex-1 h-px bg-gradient-to-r from-cyan-200 to-transparent"></div>
                <div className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full shadow-sm">
                  <div className="w-2 h-2 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full animate-ping"></div>
                  <span className="text-xs text-purple-700 font-semibold">AI-Enhanced</span>
                </div>
              </div>

              {/* Education Requirements */}
              <div className="mb-8">
                <SimpleDropdown
                  label="Education Requirements / शैक्षणिक आवश्यकता"
                  value={formData.education}
                  onChange={(value) => setFormData(prev => ({ ...prev, education: value }))}
                  options={aiSuggestions.education}
                  placeholder="Select education requirement..."
                  required={false}
                  allowCustom={true}
                  onAddCustom={(value) => handleCustomSuggestion('education', value)}
                />
              </div>

              {/* Required Skills */}
              <div className="mb-8">
                <EnhancedChipInput
                  label="Required Skills / आवश्यक कौशल्ये"
                  value={formData.requiredSkills}
                  onChange={(value) => setFormData(prev => ({ ...prev, requiredSkills: value }))}
                  suggestions={aiSuggestions.skills}
                  placeholder="Add required skills based on education..."
                  onAddCustom={(value) => handleCustomSuggestion('requiredSkills', value)}
                  maxVisibleSuggestions={10}
                  isLoading={isLoadingSkills}
                />
              </div>

              {/* Preferred Skills */}
              <div className="mb-6">
                <EnhancedChipInput
                  label="Preferred Skills / प्राधान्यकृत कौशल्ये"
                  value={formData.preferredSkills}
                  onChange={(value) => setFormData(prev => ({ ...prev, preferredSkills: value }))}
                  suggestions={aiSuggestions.preferredSkills}
                  placeholder="Add preferred skills that complement required skills..."
                  onAddCustom={(value) => handleCustomSuggestion('preferredSkills', value)}
                  maxVisibleSuggestions={10}
                  isLoading={isLoadingPreferredSkills}
                />
              </div>
            </div>

            {/* Roles & Responsibilities */}
            <div className="bg-white shadow-xl rounded-2xl p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900">Roles & Responsibilities</h2>
                <div className="flex-1 h-px bg-gradient-to-r from-indigo-200 to-transparent"></div>
                <div className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full shadow-sm">
                  <div className="w-2 h-2 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full animate-ping"></div>
                  <span className="text-xs text-purple-700 font-semibold">AI-Enhanced</span>
                </div>
              </div>

              {/* Roles */}
              <div className="mb-8">
                <EnhancedChipInput
                  label="Roles / भूमिका"
                  value={formData.requirements}
                  onChange={(value) => setFormData(prev => ({ ...prev, requirements: value }))}
                  suggestions={aiSuggestions.roles}
                  placeholder="Add roles for this position..."
                  onAddCustom={(value) => handleCustomSuggestion('requirements', value)}
                  maxVisibleSuggestions={10}
                  isLoading={isLoadingRoles}
                />
              </div>

              {/* Responsibilities */}
              <div>
                <EnhancedChipInput
                  label="Responsibilities / जबाबदाऱ्या"
                  value={formData.responsibilities}
                  onChange={(value) => setFormData(prev => ({ ...prev, responsibilities: value }))}
                  suggestions={aiSuggestions.responsibilities}
                  placeholder="Add key responsibilities..."
                  onAddCustom={(value) => handleCustomSuggestion('responsibilities', value)}
                  maxVisibleSuggestions={10}
                  isLoading={isLoadingResponsibilities}
                />
              </div>
            </div>

            {/* Benefits & Perks */}
            <div className="bg-white shadow-xl rounded-2xl p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-gradient-to-r from-pink-500 to-rose-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900">Benefits & Perks</h2>
                <div className="flex-1 h-px bg-gradient-to-r from-pink-200 to-transparent"></div>
                <div className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full shadow-sm">
                  <div className="w-2 h-2 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full animate-ping"></div>
                  <span className="text-xs text-purple-700 font-semibold">AI-Enhanced</span>
                </div>
              </div>

              <EnhancedChipInput
                label="Benefits & Perks / फायदे आणि सुविधा"
                value={formData.benefits}
                onChange={(value) => setFormData(prev => ({ ...prev, benefits: value }))}
                suggestions={aiSuggestions.benefits}
                placeholder="Add benefits and perks to attract top talent..."
                onAddCustom={(value) => handleCustomSuggestion('benefits', value)}
                maxVisibleSuggestions={12}
              />
            </div>

            {/* Application Details */}
            <div className="bg-white shadow-xl rounded-2xl p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900">Application Details</h2>
                <div className="flex-1 h-px bg-gradient-to-r from-green-200 to-transparent"></div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Application Deadline / अर्ज सादर करण्याची अंतिम तारीख
                  </label>
                  <input
                    type="date"
                    name="applicationDeadline"
                    value={formData.applicationDeadline}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Application Instructions / अर्ज सूचना
                  </label>
                  <textarea
                    name="applicationInstructions"
                    value={formData.applicationInstructions}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Any specific instructions for applicants..."
                  />
                </div>
              </div>
            </div>

            {/* AI-Generated Job Description - Moved to End */}
            <div className="bg-white shadow-xl rounded-2xl p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-violet-500 to-purple-500 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Job Description / नोकरीचे वर्णन</h2>
                  <div className="flex-1 h-px bg-gradient-to-r from-violet-200 to-transparent"></div>
                  <div className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full shadow-sm">
                    <div className="w-2 h-2 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full animate-pulse"></div>
                    <span className="text-xs text-purple-700 font-semibold">AI-Generated</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  {canGenerateDescription() && (
                    <button
                      type="button"
                      onClick={handleGenerateDescription}
                      disabled={isGeneratingDescription}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                      {isGeneratingDescription ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-sm font-medium">Generating...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          <span className="text-sm font-medium">Generate with AI</span>
                        </>
                      )}
                    </button>
                  )}
                  {formData.description && (
                    <button
                      type="button"
                      onClick={handleRegenerateDescription}
                      disabled={isGeneratingDescription}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                      {isGeneratingDescription ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-sm font-medium">Regenerating...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          <span className="text-sm font-medium">Regenerate Enhanced</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {!canGenerateDescription() && (
                <div className="mb-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-2 text-sm text-amber-700">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Fill in job title, company, industry, and at least one role/responsibility/skill to enable AI generation</span>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <div className="relative">
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows={20}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm leading-relaxed resize-y min-h-[500px] bg-gradient-to-br from-gray-50 to-white"
                    placeholder="✨ Your AI-generated job description will appear here with beautiful formatting, multiple sections, and professional styling..."
                    style={{ fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace' }}
                  />

                  {/* Character count and formatting info */}
                  <div className="absolute bottom-3 right-3 flex items-center gap-2 text-xs text-gray-500 bg-white/80 backdrop-blur-sm px-2 py-1 rounded">
                    <span>{formData.description.length} characters</span>
                    <span>•</span>
                    <span>Markdown supported</span>
                  </div>
                </div>
                {isGeneratingDescription && (
                  <div className="mt-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center gap-2 text-sm text-purple-700">
                      <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
                      <span>AI is crafting a compelling job description based on your inputs...</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Job Settings */}
            <div className="bg-white shadow-xl rounded-2xl p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-gradient-to-r from-gray-500 to-slate-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900">Job Settings</h2>
                <div className="flex-1 h-px bg-gradient-to-r from-gray-200 to-transparent"></div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={JOB_STATUS.DRAFT}>Draft</option>
                    <option value={JOB_STATUS.ACTIVE}>Active</option>
                  </select>
                </div>

                <div className="flex space-x-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="featured"
                      checked={formData.featured}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Featured job</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="urgent"
                      checked={formData.urgent}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Urgent hiring</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex flex-col sm:flex-row justify-end gap-3 sm:space-x-4">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading
                  ? (isEditMode ? 'Updating...' : 'Posting...')
                  : (isEditMode ? 'Update Job' : 'Post Job')
                }
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Footer - Hidden on mobile */}
      <div className="hidden lg:block">
        <Footer />
      </div>

      {/* Pay Now Confirmation Dialog */}
      {payNowDialog.visible && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          onClick={() => closePayNowDialog(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-lg">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Subscription Required</h3>
                <p className="text-gray-600 whitespace-pre-line">{payNowDialog.message}</p>
              </div>
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => closePayNowDialog(false)}
                className="px-4 py-2 rounded-full border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => closePayNowDialog(true, true)}
                className="px-5 py-2 rounded-full font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 shadow-lg hover:from-blue-700 hover:to-indigo-700"
              >
                Pay Now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PostJob;
